import type { JSX } from 'react';

import type { TickLabelProps } from '@visx/axis';
import { AxisBottom, AxisRight } from '@visx/axis';
import type { GridScale } from '@visx/grid';

import { Palette } from 'DesignSystem2/Theme';
import { toVariableLengthFloat } from 'utils/caseing';

export interface AxisProps {
    xTicks?: Date[];
    xTickCount?: number;
    xTickFormat?: Intl.DateTimeFormatOptions;
    yTicks?: number[];
    yTickCount?: number;
    yTickFormat?: (y: number) => string;
    xTickLength?: number;
    yTickLength?: number;
    hideXAxis?: boolean;
    hideYAxis?: boolean;
}

interface StyledAxisProps extends AxisProps {
    gridWidth: number;
    height: number;
    xScale: GridScale;
    yScale: GridScale;
}

const Axis = ({
    gridWidth,
    height,
    xScale,
    yScale,
    xTicks = undefined,
    xTickFormat = undefined,
    yTicks = undefined,
    yTickCount = undefined,
    yTickFormat = undefined,
    xTickLength = 17,
    yTickLength = 28,
    hideXAxis = false,
    hideYAxis = false,
}: StyledAxisProps): JSX.Element => {
    return (
        <>
            {!hideXAxis && (
                <AxisBottom
                    hideAxisLine={true}
                    left={0}
                    top={height}
                    scale={xScale}
                    tickValues={xTicks}
                    numTicks={5}
                    tickLength={xTickLength}
                    tickLineProps={{ strokeWidth: 0 }}
                    tickFormat={
                        xTickFormat
                            ? (date): string => {
                                  if (date instanceof Date) {
                                      return date
                                          .toLocaleString('default', xTickFormat)
                                          .replace(' AM', 'am')
                                          .replace(' PM', 'pm');
                                  }
                                  return date.toString();
                              }
                            : undefined
                    }
                    tickLabelProps={(_, i): Partial<TickLabelProps<string>> => {
                        return {
                            fill: Palette.Text.x20,
                            fontSize: 11,
                            fontFamily: 'var(--font-kalshi-sans)',
                            strokeWidth: 0,
                            textAnchor: i === 0 ? 'start' : !!xTicks && i === xTicks.length - 1 ? 'end' : 'middle',
                            pointerEvents: 'none',
                        };
                    }}
                />
            )}
            {!hideYAxis && (
                <AxisRight
                    left={gridWidth}
                    hideAxisLine={true}
                    scale={yScale}
                    numTicks={yTickCount}
                    tickLength={yTickLength}
                    tickLineProps={{ strokeWidth: 0 }}
                    tickValues={yTicks}
                    tickLabelProps={(): Partial<TickLabelProps<string>> => ({
                        strokeWidth: 4,
                        stroke: 'var(--surface-x10)',
                        fill: Palette.Text.x20,
                        paintOrder: 'stroke',
                        fontSize: 11,
                        fontFamily: 'var(--font-kalshi-sans)',
                        textAnchor: 'start',
                        verticalAnchor: 'middle',
                        pointerEvents: 'none',
                    })}
                    tickFormat={(n: number): string => (yTickFormat ? yTickFormat(n) : toVariableLengthFloat(n))}
                />
            )}
        </>
    );
};

export default Axis;
